// Hub-staff (Micro Hub Lead / Main Hub Manager) verify the agent handover OTP.
// Lists parcels awaiting OTP verification + a manual Tracking ID + OTP form.
import { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, ShieldCheck, RefreshCw, KeyRound, PackageCheck } from "lucide-react";

const ORANGE = "#FF6B00";

export default function HubHandoverVerify({ onVerified }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tid, setTid] = useState("");
  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub/handovers/pending");
      setPending(data.pending || []);
    } catch (e) {
      // 403 for non-hub roles — just render empty
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const verify = async (trackingId, otpVal) => {
    if (!trackingId || !otpVal) { toast.error("Enter Tracking ID and OTP"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/hub/handovers/verify-otp", { tracking_id: trackingId, otp: otpVal });
      toast.success(`${trackingId} received at hub (${data.status})`);
      setTid(""); setOtp("");
      load();
      onVerified?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="hub-handover-verify">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-slate-800 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" style={{ color: ORANGE }} /> Verify Agent Handover OTP
        </h3>
        <button onClick={load} className="text-slate-400 hover:text-[#FF6B00]" aria-label="Refresh" data-testid="handover-refresh">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Manual entry */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input placeholder="Tracking ID" value={tid} onChange={(e) => setTid(e.target.value.toUpperCase())}
               className="rounded-xl font-mono" data-testid="verify-tid-input" />
        <Input placeholder="6-digit OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
               className="rounded-xl font-mono w-full sm:w-36" data-testid="verify-otp-input" />
        <Button onClick={() => verify(tid.trim(), otp.trim())} disabled={submitting}
                style={{ background: ORANGE }} className="text-white rounded-xl" data-testid="verify-otp-btn">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1" /> Verify</>}
        </Button>
      </div>

      {/* Pending list */}
      {loading ? (
        <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : pending.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-6" data-testid="handover-pending-empty">No parcels awaiting handover verification.</div>
      ) : (
        <div className="space-y-2" data-testid="handover-pending-list">
          {pending.map((p) => (
            <div key={p.tracking_id} className="flex items-center justify-between border border-slate-100 rounded-xl px-3 py-2"
                 data-testid={`pending-${p.tracking_id}`}>
              <div className="min-w-0">
                <div className="font-mono text-sm font-semibold text-slate-800">{p.tracking_id}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {p.agent_name ? `Agent: ${p.agent_name} · ` : ""}{p.package_description || "Parcel"}
                </div>
              </div>
              <button onClick={() => { setTid(p.tracking_id); document.querySelector('[data-testid="verify-otp-input"]')?.focus(); }}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-orange-50 text-[#C2410C] border border-orange-200 hover:bg-orange-100 inline-flex items-center gap-1"
                      data-testid={`select-${p.tracking_id}`}>
                <PackageCheck className="h-3.5 w-3.5" /> Enter OTP
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
